import { memo, useState } from "react";
import type { MerchantsMethodsPivot } from "features/pivots/types";
import Accordion from "ui/atoms/Accordion";
import { useAccordionContext } from "ui/widgets/Accordion";
import MerchantPaymentMethodPSPSummary from "./MerchantPaymentMethodPSPSummary";
import MerchantPaymentMethodPSPDetail from "./MerchantPaymentMethodPSPDetail";
import { Box, Card, Divider, Drawer, IconButton, Typography } from "@mui/material";
import CloseIcon from '@mui/icons-material/Close';
import { USE_PAYMENT_METHODS_V2 } from "../paymentMethodsUi";


type Props = MerchantsMethodsPivot & {
    selected: boolean;
    onSelectionChange: (id: string, checked: boolean) => void;
    bulkState?: Pick<MerchantsMethodsPivot, 'enabled' | 'MOR'>;
};

const MerchantPaymentMethodPSP = ({ selected, onSelectionChange, bulkState, ...merchantMethod }: Props) => {
    const { expanded, handleChange } = useAccordionContext();
    const [editorOpen, setEditorOpen] = useState(false);
    const displayedMethod = bulkState ? { ...merchantMethod, ...bulkState } : merchantMethod;

    const compactEditor = (
        <>
            <MerchantPaymentMethodPSPSummary
                {...displayedMethod}
                selected={selected}
                onSelectionChange={checked => onSelectionChange(String(merchantMethod.coreId), checked)}
            />
            <Drawer
                anchor='right'
                open={editorOpen}
                onClose={() => setEditorOpen(false)}
                onClick={event => event.stopPropagation()}
                PaperProps={{
                    sx: {
                        width: { xs: '100vw', md: '72vw' },
                        maxWidth: 1100,
                        overflow: 'hidden'
                    }
                }}
            >
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, px: 3, py: 2 }}>
                    <Box sx={{ flex: 1 }}>
                        <Typography variant='h5'>{merchantMethod.paymentMethodName}</Typography>
                        <Typography color='text.secondary'>
                            {merchantMethod.paymentMethodTag} · {merchantMethod.paymentMethodPSP}
                        </Typography>
                    </Box>
                    <IconButton aria-label='Close payment method manager' onClick={() => setEditorOpen(false)}>
                        <CloseIcon />
                    </IconButton>
                </Box>
                <Divider />
                <Box sx={{
                    p: 3,
                    width: '100%',
                    minWidth: 0,
                    overflowX: 'hidden',
                    overflowY: 'auto'
                }}>
                    <MerchantPaymentMethodPSPDetail {...displayedMethod} />
                </Box>
            </Drawer>
        </>
    );

    return (
        <Card
        role={USE_PAYMENT_METHODS_V2 ? 'button' : undefined}
        tabIndex={USE_PAYMENT_METHODS_V2 ? 0 : undefined}
        onClick={USE_PAYMENT_METHODS_V2 ? () => setEditorOpen(true) : undefined}
        onKeyDown={USE_PAYMENT_METHODS_V2 ? event => {
            if (event.key === 'Enter' || event.key === ' ') setEditorOpen(true);
        } : undefined}
        sx={{
            position: 'relative',
            pl: 5,
            padding: '10px',
            cursor: USE_PAYMENT_METHODS_V2 ? 'pointer' : 'default',
            transition: 'box-shadow .2s ease, transform .2s ease',
            '&:hover': USE_PAYMENT_METHODS_V2 ? {
                boxShadow: 4,
                transform: 'translateY(-1px)'
            } : undefined,
            marginTop: '15px',
            marginBottom: '15px',
        }}>
        {USE_PAYMENT_METHODS_V2 ? compactEditor : (
            <Accordion
                expanded={Object.is(expanded, merchantMethod.coreId)}
                onChange={handleChange(merchantMethod.coreId)}
                AccordionSummaryProps={{
                    children: <MerchantPaymentMethodPSPSummary {...displayedMethod} />,
                    'aria-controls': `${merchantMethod.paymentMethodPSP} content`,
                    id: `${merchantMethod.paymentMethodPSP} summary`
                }}
            >
                <MerchantPaymentMethodPSPDetail {...displayedMethod} />
            </Accordion>
        )}
        </Card>
    );
};

export default memo(MerchantPaymentMethodPSP);
